import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import routes from 'src/config/routes'
import { findTreatmentInCategorizedTreatments } from 'src/helpers/treatment'
import { analyticsEventSelectedTreatment } from 'src/services/analytics'
import {
  resetSelectedBookable,
  setIsManualReviewRequired,
  setTreatment,
  useChoiceDispatch,
  useChoiceState,
} from '@context/ChoiceContext'
import {
  clearPractitioners,
  resetSelectedPractitionersFor,
  usePractitionersDispatch,
} from '@context/PractitionersContext'
import {
  clearState as clearUpsellState,
  clearVitaminDUpsellInformation,
  setCanTheoreticallyBeUpsold,
  useUpsellDispatch,
} from '@context/UpsellContext'
import useCategorizedTreatments from './useCategorizedTreatments'
import {
  clearState as clearPainDetailsState,
  setShowPainTreatmentForm,
  usePainDetailsDispatch,
} from '@context/PainDetailsContext'
import { useMakeAdditionalActionIfChildProphylaxis } from './useMakeAdditionalActionIfChildProphylaxis'
import useTreatmentUpsellDetails from './useTreatmentUpsellDetails'

// This hook returns method used to select treatment because there are additional state updates when selecting the treatment.
export const useSelectTreatment = (onFinishCallback?: () => void) => {
  const { data } = useCategorizedTreatments()
  const { treatmentBundleTypeReferenceId } = useChoiceState()
  const { data: upsellDetails } = useTreatmentUpsellDetails()

  const choiceDispatch = useChoiceDispatch()
  const practitionersDispatch = usePractitionersDispatch()
  const upsellDispatch = useUpsellDispatch()
  const painDetailsDispatch = usePainDetailsDispatch()
  const makeAdditionalActionIfChildProphylaxis = useMakeAdditionalActionIfChildProphylaxis()
  const history = useHistory()
  const { t } = useTranslation()
  const { treatmentBundleToBeUpsoldFromReferenceId, treatmentBundleToBeUpsoldTo } = upsellDetails || {}

  return (id: string) => {
    const treatment = findTreatmentInCategorizedTreatments(data || [], id)
    if (!treatment) return
    const treatmentChanged = treatmentBundleTypeReferenceId !== treatment.referenceId
    const isTreatmentUpellableFromOrUpsellableTo =
      !!(treatment.referenceId === treatmentBundleToBeUpsoldFromReferenceId) ||
      !!(treatment.referenceId === treatmentBundleToBeUpsoldTo?.referenceId)
    const treatmentIsUpsellableButPreviousUpsellIsForOtherTreatment =
      treatment.canTheoreticallyBeUpsold && upsellDetails?.referenceId !== treatment.referenceId

    makeAdditionalActionIfChildProphylaxis(treatment.treatmentPurposeReferenceId)
    choiceDispatch(setTreatment(treatment.referenceId))
    painDetailsDispatch(clearPainDetailsState())
    painDetailsDispatch(setShowPainTreatmentForm(treatment.showPainTreatmentForm))
    upsellDispatch(setCanTheoreticallyBeUpsold(treatment.canTheoreticallyBeUpsold))
    if (
      upsellDetails &&
      !isTreatmentUpellableFromOrUpsellableTo &&
      !treatmentIsUpsellableButPreviousUpsellIsForOtherTreatment
    )
      upsellDispatch(clearUpsellState())
    if (treatmentChanged) {
      practitionersDispatch(clearPractitioners())
      practitionersDispatch(resetSelectedPractitionersFor())
      choiceDispatch(resetSelectedBookable())
      upsellDispatch(clearVitaminDUpsellInformation())
    }
    choiceDispatch(setIsManualReviewRequired(treatment.manualReviewRequired || false))
    analyticsEventSelectedTreatment(t(treatment.titleKey || ''))

    onFinishCallback ? onFinishCallback() : history.push(routes.patient)
  }
}
